"use strict";
import { Token } from "@uniswap/sdk-core";
import { NativeCurrency as TopazNativeCurrency } from "features/tokens/NativeCurrency";
import { getNativeAddress } from "constants/uniswapAddresses";
import { fetchTradingApiIndicativeQuoteIgnoring404 } from "data/apiClients/tradingApi/useTradingApiIndicativeQuoteQuery";
import {
  TokenDocument
} from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { fetchTokenData } from "features/routing/api/bscSubgraph";
import { isBscChain } from "constants/bscContracts";
import { TradeType } from "data/tradingApi/__generated__";
import { fromGraphQLChain } from "features/chains/utils";
import { currencyIdToContractInput, gqlTokenToCurrencyInfo } from "features/dataApi/utils";
import { getOnChainBalancesFetch } from "features/portfolio/api";
import { ValueType, getCurrencyAmount } from "features/tokens/getCurrencyAmount";
import { STABLECOIN_AMOUNT_OUT } from "features/transactions/hooks/useUSDCPrice";
import { toTradingApiSupportedChainId } from "features/transactions/swap/utils/tradingApi";
import { currencyIdToAddress, currencyIdToChain, isNativeCurrencyAddress } from "utils/uniswapCurrencyId";
import { getTokenLogoUrl } from "constants/tokenLogos";
import { logger } from "utilities/src/logger/logger";
export async function fetchOnChainBalances({
  apolloCache,
  cachedPortfolio,
  accountAddress,
  currencyIds
}) {
  const onchainBalancesByCurrencyId = /* @__PURE__ */ new Map();
  logger.debug("getOnChainBalances.ts", "getOnChainBalances", "[ITBU] Fetching onchain balances", currencyIds);
  await Promise.all(
    Array.from(currencyIds).map(async (currencyId) => {
      const currencyAddress = currencyIdToAddress(currencyId);
      const chainId = currencyIdToChain(currencyId);
      if (!currencyAddress || !chainId) {
        logger.error(new Error("Unable to parse `currencyId`"), {
          tags: { file: "fetchOnChainBalances.ts", function: "fetchOnChainBalances" },
          extra: { currencyId }
        });
        return;
      }
      const { balance: onchainBalance } = await getOnChainBalancesFetch({
        currencyAddress,
        chainId,
        currencyIsNative: isNativeCurrencyAddress(chainId, currencyAddress),
        accountAddress
      });
      let currencyInfo;
      let token;
      if (isBscChain(chainId)) {
        if (isNativeCurrencyAddress(chainId, currencyAddress)) {
          const _nativeCurrency = getNativeAddress(chainId);
          currencyInfo = {
            currency: TopazNativeCurrency.onChain(chainId),
            currencyId,
            logoUrl: "/images/tokens/bnb-logo.png",
            safetyLevel: void 0
          };
        } else {
          const tokenData = await fetchTokenData(currencyAddress);
          if (!tokenData) {
            logger.warn("fetchOnChainBalances.ts", "fetchOnChainBalances", "No token found in BSC subgraph", { currencyId });
            return;
          }
          const bscToken = new Token(
            chainId,
            tokenData.id,
            parseInt(tokenData.decimals),
            tokenData.symbol,
            tokenData.name
          );
          currencyInfo = {
            currency: bscToken,
            currencyId,
            logoUrl: getTokenLogoUrl(currencyAddress),
            safetyLevel: void 0
          };
        }
      } else {
        token = apolloCache.readQuery({
          query: TokenDocument,
          variables: currencyIdToContractInput(currencyId)
        })?.token;
        if (!token) {
          logger.warn("fetchOnChainBalances.ts", "fetchOnChainBalances", "No `token` found", { currencyId });
          return;
        }
        currencyInfo = gqlTokenToCurrencyInfo(token);
        if (!currencyInfo) {
          logger.warn("fetchOnChainBalances.ts", "fetchOnChainBalances", "No `currency` found");
          return;
        }
      }
      const onchainQuantityCurrencyAmount = getCurrencyAmount({
        value: onchainBalance,
        valueType: ValueType.Raw,
        currency: currencyInfo.currency
      });
      const quantity = onchainQuantityCurrencyAmount?.toExact();
      const denominatedValue = onchainQuantityCurrencyAmount && !isBscChain(chainId) && token ? await getDenominatedValue({
        onchainQuantityCurrencyAmount,
        token,
        cachedPortfolio
      }) : void 0;
      onchainBalancesByCurrencyId.set(currencyId, {
        currencyAddress,
        chainId,
        rawBalance: onchainBalance,
        quantity: quantity ? parseFloat(quantity) : void 0,
        denominatedValue
      });
    })
  );
  logger.debug(
    "getOnChainBalances.ts",
    "getOnChainBalances",
    "[ITBU] Onchain balances fetched",
    JSON.stringify(Object.fromEntries(onchainBalancesByCurrencyId))
  );
  return onchainBalancesByCurrencyId;
}
async function getDenominatedValue({
  onchainQuantityCurrencyAmount,
  token,
  cachedPortfolio
}) {
  const inferredDenominatedValue = getInferredCachedDenominatedValue({
    cachedPortfolio,
    token,
    onchainQuantityCurrencyAmount
  });
  if (inferredDenominatedValue) {
    return inferredDenominatedValue;
  }
  const extra = {
    cachedPortfolio,
    token,
    onchainQuantityCurrencyAmount
  };
  const chainId = toTradingApiSupportedChainId(fromGraphQLChain(token.chain));
  if (!chainId) {
    logger.error(new Error("[ITBU] No `chainId` found"), {
      tags: {
        file: "fetchOnChainBalances.ts",
        function: "getDenominatedValue"
      },
      extra
    });
    return void 0;
  }
  const universeChainId = fromGraphQLChain(token.chain);
  if (!universeChainId) {
    return void 0;
  }
  const tokenAddress = token.address ?? getNativeAddress(universeChainId);
  const stablecoinCurrency = STABLECOIN_AMOUNT_OUT[universeChainId]?.currency;
  if (!stablecoinCurrency) {
    logger.error(new Error("[ITBU] No `stablecoinCurrency` found"), {
      tags: {
        file: "fetchOnChainBalances.ts",
        function: "getDenominatedValue"
      },
      extra
    });
    return void 0;
  }
  const indicativeQuote = await fetchIndicativeQuote({
    type: TradeType.EXACT_INPUT,
    amount: onchainQuantityCurrencyAmount.quotient.toString(),
    tokenInChainId: chainId,
    tokenOutChainId: chainId,
    tokenIn: tokenAddress,
    tokenOut: stablecoinCurrency.address
  });
  const amountOut = indicativeQuote?.output.amount;
  if (!amountOut) {
    return void 0;
  }
  const currencyAmountOut = getCurrencyAmount({
    value: amountOut,
    valueType: ValueType.Raw,
    currency: stablecoinCurrency
  });
  return currencyAmountOut ? {
    value: parseFloat(currencyAmountOut.toFixed()),
    currency: "USD"
  } : void 0;
}
function getInferredCachedDenominatedValue({
  cachedPortfolio,
  token,
  onchainQuantityCurrencyAmount
}) {
  const cachedTokenBalance = cachedPortfolio?.tokenBalances?.find(
    (balance) => balance?.token?.address === token.address && balance?.token?.chain === token.chain
  );
  if (cachedTokenBalance?.denominatedValue && cachedTokenBalance.quantity) {
    const onchainQuantity = onchainQuantityCurrencyAmount.toExact();
    return {
      value: cachedTokenBalance.denominatedValue.value * parseFloat(onchainQuantity) / cachedTokenBalance.quantity,
      currency: cachedTokenBalance.denominatedValue.currency ?? "USD"
    };
  }
  return void 0;
}
async function fetchIndicativeQuote(params) {
  try {
    return await fetchTradingApiIndicativeQuoteIgnoring404({ params });
  } catch (error) {
    logger.error(error, {
      tags: {
        file: "fetchOnChainBalances.ts",
        function: "getIndicativeQuote"
      },
      extra: {
        params
      }
    });
    return void 0;
  }
}
